import type { ComponentProps, ElementType, ReactNode } from 'react';

import { cn } from '../../lib/utils';

export type EmptyStateProps = Omit<ComponentProps<'div'>, 'title'> & {
  /** Phosphor icon in the Pale Sage circle (Figma: film-strip). */
  icon: ElementType<{ className?: string }>;
  /** Figma "Title" - what goes here ("No reels yet"). Heading/H4. */
  title: ReactNode;
  /** Figma "Body" - why it matters and what to do. Body/Medium text/secondary. */
  description?: ReactNode;
  /** One Primary Button, optionally a Ghost secondary action after it. */
  actions?: ReactNode;
  /** Heading element for the title - match the page outline. */
  titleAs?: 'h2' | 'h3' | 'h4' | 'p';
};

/**
 * Shown when a list, table or canvas has no content (Figma: Components ↳
 * Grid & Table ↳ Empty State, 58:160). Centred column, 400 wide, 40px above
 * and below, 12px apart: a 56px brand/subtle circle with a 28px icon/brand
 * icon, the title, the body, and the actions (8px further down, 8px apart).
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  actions,
  titleAs: Title = 'h3',
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'mx-auto flex w-full max-w-100 flex-col items-center gap-3 py-10 text-center',
        className,
      )}
      {...props}
    >
      <span
        data-slot="empty-state-illustration"
        className="bg-brand-subtle text-icon-brand flex size-14 items-center justify-center rounded-full"
      >
        <Icon aria-hidden className="size-7" />
      </span>
      <Title data-slot="empty-state-title" className="text-heading-h4 text-text-primary">
        {title}
      </Title>
      {description && (
        <p data-slot="empty-state-body" className="text-body-md text-text-secondary">
          {description}
        </p>
      )}
      {actions && (
        <div data-slot="empty-state-actions" className="flex items-start gap-2 pt-2">
          {actions}
        </div>
      )}
    </div>
  );
}
